/**
 * Sales dashboard + priority leads URL params.
 * Prefer URL over location.state so back/refresh keep filters.
 */

export function getSalesDashboardFiltersFromSearch(searchParams) {
  const projectID = searchParams.get('projectID') || searchParams.get('project_id');
  const month = searchParams.get('month');
  return {
    projectID: projectID ? Number(projectID) || projectID : null,
    month: month || null
  };
}

export function buildSalesDashboardFilterParams(searchParams, { projectID, month }) {
  const next = new URLSearchParams(searchParams);
  if (projectID != null && projectID !== '') {
    next.set('projectID', String(projectID));
  } else {
    next.delete('projectID');
    next.delete('project_id');
  }
  if (month) {
    next.set('month', String(month));
  } else {
    next.delete('month');
  }
  return next;
}

/**
 * How many follow-up IDs may travel in the URL.
 *
 * An escalation card can hold hundreds of follow-ups. Serialising every ID
 * produced a multi-KB URL, which lead details then embedded a second time in
 * ?returnTo= — enough for Chrome to kill the tab ("Aw, Snap", error code 5).
 * Past the cap the IDs ride in history state only; the list is still narrowed
 * server-side by salesID + filterType, so the visible rows are the same.
 */
export const PRIORITY_FOLLOWUP_IDS_URL_LIMIT = 25;

/**
 * Drop followUpIds only when it is over the cap, so a returnTo built from a
 * stale (pre-cap) URL cannot re-inflate it. Short lists are left alone and keep
 * surviving a back-navigation.
 */
export function stripOversizedFollowUpIds(search = '') {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return '';
  const params = new URLSearchParams(raw);
  const ids = params.get('followUpIds');
  if (!ids || ids.split(',').length <= PRIORITY_FOLLOWUP_IDS_URL_LIMIT) {
    return `?${raw}`;
  }
  params.delete('followUpIds');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Build query string for priority leads list from filter object. */
export function buildPriorityLeadsSearch({
  salesID,
  salesName,
  filterType,
  followUpDate,
  followUpIds,
  projectID,
  q
} = {}) {
  const params = new URLSearchParams();
  if (salesID != null && salesID !== '') params.set('salesID', String(salesID));
  if (salesName) params.set('salesName', String(salesName));
  if (filterType) params.set('filterType', String(filterType));
  if (followUpDate) params.set('followUpDate', String(followUpDate));
  if (projectID != null && projectID !== '') params.set('projectID', String(projectID));
  if (q) params.set('q', String(q));
  if (Array.isArray(followUpIds) && followUpIds.length) {
    const ids = followUpIds.filter(Boolean);
    if (ids.length && ids.length <= PRIORITY_FOLLOWUP_IDS_URL_LIMIT) {
      params.set('followUpIds', ids.join(','));
    }
  }
  return params;
}

/** Read priority leads filters from URL (with location.state fallback for old links). */
export function getPriorityLeadsFilters(location) {
  const params = new URLSearchParams(location?.search || '');
  const state = location?.state || {};

  const followUpIdsRaw = params.get('followUpIds');
  const followUpIdsFromQuery = followUpIdsRaw
    ? followUpIdsRaw
        .split(',')
        .map((id) => Number(id) || id)
        .filter(Boolean)
    : null;

  return {
    salesID: params.get('salesID') || state.salesID || null,
    salesName: params.get('salesName') || state.salesName || null,
    filterType: params.get('filterType') || state.filterType || null,
    followUpDate: params.get('followUpDate') || state.followUpDate || null,
    projectID: params.get('projectID') || state.projectID || null,
    followUpIds: followUpIdsFromQuery || state.followUpIds || null,
    q: params.get('q') || ''
  };
}

const SALES_SECONDARY_TABS = new Set([
  'Leads',
  'Prelead List',
  'Unit Inventory',
  'Under Consideration',
  'Closed Won',
  'Revenue',
  'Targets & Incentives',
  'Property Templates'
]);

const SALES_SUB_VIEWS = new Set(['dashboard', 'progression', 'leads', 'tasks']);

export function getSalesMainTabFromSearch(searchParams, { localStorageFallback } = {}) {
  const tab = searchParams.get('tab');
  if (SALES_SECONDARY_TABS.has(tab)) return tab;
  if (localStorageFallback && SALES_SECONDARY_TABS.has(localStorageFallback)) {
    return localStorageFallback;
  }
  return 'Leads';
}

export function getSalesSubViewFromSearch(searchParams, { localStorageFallback } = {}) {
  const view = searchParams.get('view');
  if (SALES_SUB_VIEWS.has(view)) return view;
  // legacy: secondaryTab in state implied leads view
  if (localStorageFallback === 'dashboard' || localStorageFallback === 'progression') {
    return localStorageFallback;
  }
  return null;
}

export function buildSalesMainTabParams(
  searchParams,
  { view, tab, projectID, month, clearListProject = false } = {}
) {
  const next = new URLSearchParams(searchParams);
  if (view) {
    next.set('view', view);
  }
  if (tab) {
    next.set('tab', tab);
  }

  // Dashboard filters (projectID/month) belong on dashboard/progression only.
  // Leads tab defaults to All Projects; dashboard metric redirects use project_id instead.
  if (view === 'leads') {
    next.delete('projectID');
    next.delete('month');
    if (clearListProject) {
      next.delete('project_id');
    }
  } else {
    if (projectID != null && projectID !== '') {
      next.set('projectID', String(projectID));
    } else if (projectID === null || projectID === '') {
      next.delete('projectID');
      next.delete('project_id');
    }
    if (month) {
      next.set('month', String(month));
    } else if (month === null || month === '') {
      next.delete('month');
    }
  }

  return next;
}

/** Serialize SalesList activeFilters into URL search params (subset). */
export function buildSalesListFilterParams(searchParams, filters = {}) {
  const next = new URLSearchParams(searchParams);
  const setOrDelete = (key, value) => {
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      next.delete(key);
    } else if (Array.isArray(value)) {
      next.set(key, value.join(','));
    } else {
      next.set(key, String(value));
    }
  };

  setOrDelete('project', filters.project);
  setOrDelete('sales', filters.sales);
  setOrDelete('salesId', filters.salesId);
  setOrDelete('status', filters.status);
  setOrDelete('source', filters.source);
  setOrDelete('closureDate', filters.closureDate);
  setOrDelete('lastFollowUp', filters.lastFollowUp);
  setOrDelete('nextFollowUp', filters.nextFollowUp);
  setOrDelete('nextFollowUpFrom', filters.nextFollowUpFrom);
  setOrDelete('nextFollowUpTo', filters.nextFollowUpTo);
  setOrDelete('createdOn', filters.createdOn);
  setOrDelete('createdFrom', filters.createdFrom);
  setOrDelete('createdTo', filters.createdTo);
  setOrDelete('qualificationTags', filters.qualificationTags);
  return next;
}

export function getSalesListFiltersFromSearch(searchParams) {
  const statusRaw = searchParams.get('status');
  const tagsRaw = searchParams.get('qualificationTags');
  return {
    project: searchParams.get('project') || null,
    sales: searchParams.get('sales') || null,
    salesId: searchParams.get('salesId') || null,
    status: statusRaw ? statusRaw.split(',').filter(Boolean) : [],
    source: searchParams.get('source') || null,
    closureDate: searchParams.get('closureDate') || null,
    lastFollowUp: searchParams.get('lastFollowUp') || null,
    nextFollowUp: searchParams.get('nextFollowUp') || null,
    nextFollowUpFrom: searchParams.get('nextFollowUpFrom') || null,
    nextFollowUpTo: searchParams.get('nextFollowUpTo') || null,
    createdOn: searchParams.get('createdOn') || null,
    createdFrom: searchParams.get('createdFrom') || null,
    createdTo: searchParams.get('createdTo') || null,
    qualificationTags: tagsRaw ? tagsRaw.split(',').filter(Boolean) : []
  };
}
